import React, { useRef } from 'react'
import { useSelector } from 'react-redux'

import Avatar from 'components/common/Avatar'
import { Namechip } from 'components/common/Namechip'
import { useEmoji, useGiphy, useGesture } from 'components/Tiles/hooks'
import { ids } from 'constants/elementIdentifiers'
import { mediaStates } from 'constants/twilio'
import { userTypes } from 'constants/user'
import { isEmpty } from 'helpers/lodash'
import { isDemo } from 'helpers/miscellaneous'
// import { toggleRemoteParticipantVideoMedia } from 'helpers/twilio'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { SECONDARY_DEVICE } from 'services/webSockets/roomState/constants'
import { getParticipantInfo } from 'store/selectors/Room'
import {
    _isIframeShared,
    _roomStateMsgData,
    _websocketNoteMsg,
    _websocketUserId,
} from 'store/selectors/WebSocket'
import { isDesktop } from 'utils/device'

import ActiveSpeakerMobile from './components/ActiveSpeaker/ActiveSpeakerMobile'
import { useAudioToggle } from './hooks/useAudioToggle'
import useElsewhereMsgHandler from './hooks/useElsewhereMsgHandler'
import {
    TileWrapper,
    Wrapper,
    RectangleOuter,
    RectangleInner,
    Indicators,
    NameChipContainer,
    AudioIndicatorContainer,
    NetworkIndicatorContainer,
    ScreenShareIndicator,
    ReactionWrapper,
    NameChipContainerMobile,
} from './styles'

import Audio from '../../AudioLevelIndicator/AudioLevelIndicator'
import { Emoji, Gesture, Giphy } from '../components'
import Network from '../Indicators/Network'
import Note from '../Indicators/Note'
import FoodButton from '../Modes/Greetings/FoodButton'
import FoodSplotch from '../Modes/Greetings/FoodSplotch'
import LetsVibe from '../Modes/LetsVibe'
import {
    MobileStudentAvatarWrapper,
    MobileStudentWrapper,
    AvatarWrapper,
    VideoWrapper,
} from '../styles'

const Student = ({
    identity,
    audioTrack,
    children,
    className,
    isLocal,
    isVideoEnabled,
    studentsCount,
    isArtefactMode,
    isActiveSpeaker,
}) => {
    // React.useEffect(() => {
    //     // switch on video media on component mount
    //     if (!isLocal) {
    //         console.log(`switching on ${identity.fullName} video`)
    //         toggleRemoteParticipantVideoMedia(identity.uuid, mediaStates.ON)
    //     }

    //     return () => {
    //         // switch off video media on component unmount
    //         if (!isLocal) {
    //             console.log(`switching off ${identity.fullName} video`)

    //             toggleRemoteParticipantVideoMedia(
    //                 identity.uuid,
    //                 mediaStates.OFF
    //             )
    //         }
    //     }
    // }, []) // dont put any dependency here

    let tile = useRef(null)

    const participantInfo = useSelector(state =>
        getParticipantInfo(state, identity?.uuid)
    )

    const notes = useSelector(_websocketNoteMsg)
    const userId = useSelector(_websocketUserId)

    const data = useSelector(_roomStateMsgData)

    const iframeShared = useSelector(_isIframeShared)

    const emojiData = useEmoji(isLocal, identity)
    const giphyData = useGiphy(isLocal, identity)
    const gestureData = useGesture(isLocal, identity)

    const userNotes = React.useMemo(() => {
        return isDemo && userId
            ? notes.filter(note => {
                  return note.note.user_id === userId
              })
            : notes.filter(note => {
                  return note.note.user_id === identity.uuid
              })
    }, [notes, userId])

    const { isQuizMode, userType, isWhiteboardMode } = useVideoContext()

    const isTeacher = userType === userTypes.TEACHER

    const isSecondaryDevice = data === SECONDARY_DEVICE && isTeacher

    // Toggle audio when teacher mute/unmute student
    useAudioToggle(isLocal, identity?.uuid)

    const { isAway } = useElsewhereMsgHandler(identity)

    const extraProps = {
        uuid: identity?.uuid,
    }

    const vibeProps = {
        isLocal,
        ...extraProps,
    }

    const greetingsProps = {
        ...vibeProps,
    }

    console.count('Student Component')

    const nameChip = (
        <Namechip identity={identity} isAway={isAway} isLocal={isLocal} />
    )

    if (!isDesktop) {
        return (
            <MobileStudentWrapper>
                {children}
                <MobileStudentAvatarWrapper
                    visible={!isVideoEnabled || !isLocal}
                >
                    <Avatar uuid={identity.uuid} />
                </MobileStudentAvatarWrapper>
                <ActiveSpeakerMobile {...extraProps} />
                <NameChipContainerMobile>{nameChip}</NameChipContainerMobile>
            </MobileStudentWrapper>
        )
    }

    const indicators = (
        <>
            <AudioIndicatorContainer>
                <Audio
                    isLocal={isLocal}
                    audioTrack={audioTrack}
                    background="white"
                    identity={identity}
                />
            </AudioIndicatorContainer>
            <NetworkIndicatorContainer>
                <Network studentsCount={studentsCount} />
            </NetworkIndicatorContainer>
            {participantInfo?.isScreenSharing && <ScreenShareIndicator />}
        </>
    )

    return (
        <Wrapper
            isVideoEnabled={isVideoEnabled}
            className={className}
            isLocal={isLocal}
            isArtefactMode={isArtefactMode || iframeShared}
        >
            <RectangleOuter>
                <RectangleInner
                    ref={tile}
                    id={`${ids.TILE_PREFIX}${identity?.uuid}`}
                    isAway={isAway}
                >
                    <TileWrapper
                        isSpeaking={isActiveSpeaker}
                        isQuizMode={isQuizMode}
                    >
                        <AvatarWrapper className="avater-container">
                            <Avatar uuid={identity.uuid} />
                        </AvatarWrapper>

                        <VideoWrapper className="video-container">
                            {children}
                        </VideoWrapper>

                        {emojiData.show ? (
                            <ReactionWrapper>
                                <Emoji emoji={emojiData.id} />
                            </ReactionWrapper>
                        ) : null}

                        {giphyData.show ? (
                            <ReactionWrapper isGiphy>
                                <Giphy gifId={giphyData.id} />
                            </ReactionWrapper>
                        ) : null}

                        <FoodSplotch {...greetingsProps} />

                        <ReactionWrapper>
                            <Gesture
                                visible={gestureData.show}
                                gestureKey={gestureData.gestureKey}
                                gestureType={gestureData.gestureType}
                                gesturePK={gestureData.gesturePK}
                                isLocal={isLocal}
                            />
                        </ReactionWrapper>
                    </TileWrapper>

                    <Indicators studentsCount={studentsCount}>
                        {!isEmpty(userNotes) ? (
                            <Note
                                studentsCount={studentsCount}
                                userNotes={userNotes}
                                userId={identity.uuid}
                                userName={identity.fullName}
                            />
                        ) : null}
                    </Indicators>
                    <LetsVibe
                        alternate={indicators}
                        {...vibeProps}
                        id={`vibe-${identity.uuid}`}
                    />
                    <FoodButton {...greetingsProps} isStudent />
                    <NameChipContainer>{nameChip}</NameChipContainer>
                </RectangleInner>
            </RectangleOuter>
        </Wrapper>
    )
}

export default Student
